import { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import {
  getMessageById,
  formatMessageId,
  getDirectionLabel,
  getMessageTypeLabel,
  secsData,
} from '../utils/secsDataLoader';
import { SecsMessage } from '../types/secs';
import { resolveMessageDataItem } from '../utils/dataItems';
import DataItemCodesTable from '../components/DataItemCodesTable';

export default function MessageViewer() {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const { addToRecentlyViewed, toggleFavorite, favorites } = useAppStore();
  const [message, setMessage] = useState<SecsMessage | null>(null);
  const [copiedFormat, setCopiedFormat] = useState(false);

  useEffect(() => {
    if (messageId) {
      const msg = getMessageById(messageId);
      if (msg) {
        setMessage(msg);
        addToRecentlyViewed(messageId);
      }
    }
  }, [messageId, addToRecentlyViewed]);

  if (!message) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Message Not Found</h2>
          <p className="text-[var(--color-text-secondary)]">
            The requested message could not be found.
          </p>
        </div>
      </div>
    );
  }

  const msgId = formatMessageId(message.stream, message.function);
  const isFavorite = favorites.includes(messageId || '');
  const direction = getDirectionLabel(message.direction);
  const messageType = getMessageTypeLabel(message.messageType);

  const handleCopyFormat = () => {
    navigator.clipboard.writeText(message.format);
    setCopiedFormat(true);
    setTimeout(() => setCopiedFormat(false), 2000);
  };

  const handleRelatedClick = () => {
    if (message.relatedMessage) {
      navigate(`/message/${message.relatedMessage}`);
    }
  };

  const dataTypeName = (typeCode: string) => {
    return secsData.dataTypes[typeCode]?.name || typeCode;
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              <span className="font-mono text-primary-600 dark:text-primary-400">{msgId}</span>
              {' - '}
              {message.name}
            </h1>
            <p className="text-lg text-[var(--color-text-secondary)]">{message.description}</p>
          </div>

          <button
            onClick={() => toggleFavorite(messageId || '')}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg
              className={`w-6 h-6 ${
                isFavorite
                  ? 'fill-yellow-500 stroke-yellow-500'
                  : 'fill-none stroke-current'
              }`}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        </div>

        {/* Metadata Badges */}
        <div className="flex flex-wrap gap-3">
          <span className={`badge ${messageType.color}`}>
            {messageType.text}
          </span>

          <span className={`badge ${direction.color}`}>
            {direction.text}
          </span>

          {message.relatedMessage && (
            <button
              onClick={handleRelatedClick}
              className="badge badge-primary hover:opacity-80 cursor-pointer"
            >
              {message.messageType === 'primary' ? 'Reply: ' : 'Primary: '}
              {message.relatedMessage}
            </button>
          )}
        </div>
      </div>

      {/* Message Format */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Message Format</h2>
          <button
            onClick={handleCopyFormat}
            className="btn btn-secondary text-sm"
          >
            {copiedFormat ? (
              <>
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
        <pre className="bg-[var(--color-bg-tertiary)] p-4 rounded-lg overflow-x-auto font-mono text-sm">
          {message.format}
        </pre>
      </div>

      {/* Data Items */}
      {message.dataItems.length > 0 && (
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Data Items</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Length</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Description</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Required</th>
                </tr>
              </thead>
              <tbody>
                {message.dataItems.map((item, index) => {
                  const resolved = resolveMessageDataItem(item);
                  const codes = resolved.codes ?? resolved.definition?.codes;
                  return (
                    <Fragment key={index}>
                      <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] transition-colors">
                        <td className="py-3 px-4 font-mono font-semibold text-sm">
                          {resolved.definition ? (
                            <button
                              onClick={() => navigate(`/data-items/${resolved.name}`)}
                              className="link"
                            >
                              {resolved.name}
                            </button>
                          ) : (
                            resolved.name
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded font-mono text-xs">
                            {resolved.type}
                          </span>
                          <span className="ml-2 text-xs text-[var(--color-text-tertiary)]">
                            {dataTypeName(resolved.type)}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{resolved.length || '-'}</td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">{resolved.description}</td>
                        <td className="py-3 px-4 text-center">
                          {resolved.required ? (
                            <span className="inline-block w-5 h-5 bg-green-500 rounded-full" title="Required">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          ) : (
                            <span className="inline-block w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full" title="Optional"></span>
                          )}
                        </td>
                      </tr>
                      {codes && codes.length > 0 && (
                        <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]/40">
                          <td colSpan={5} className="py-3 px-4">
                            <div className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase mb-2">
                              {resolved.name} coded values
                            </div>
                            <DataItemCodesTable codes={codes} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage Information */}
      {message.usage && (
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">Usage</h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed">{message.usage}</p>
        </div>
      )}

      {/* Notes */}
      {message.notes && message.notes.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-3">Notes</h2>
          <ul className="list-disc list-inside space-y-2 text-[var(--color-text-secondary)]">
            {message.notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
